import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Immutable, State } from "@hookstate/core"
import { useNotificationsDataState } from "../../globalStates/NotificationDataState"
import { replacePlaceholder, useLanguageState } from "../../globalStates/LanguageState"
import { RefCategory, ReferenceDataState, useReferenceDataState } from "../../globalStates/ReferenceDataState"
import { Category } from "../../globalStates/ExhibitorDataTypes"
import { addElement, removeElementById } from "../../globalStates/utils"
import { isNullOrEmptyRO } from "../../utils/Arrays"
import TreeCheck, { TreeData, TreeItemData } from "../../components/TreeCheck"
import SearchField from "../../components/Search"

function selectAllParents(node: TreeItemData) {
    if (node.parent) {
        // console.log('parent ' + node.parent.id + '/' + node.parent.text + ' checked')
        node.parent.checked = true
        selectAllParents(node.parent)
    }
}

function isEligible(refCategory: Immutable<RefCategory>, filter: string) {
    const eligible = refCategory.name && refCategory.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0

    return eligible
}

function makeNode(
    refCategory: Immutable<RefCategory>,
    subNodes: TreeItemData[],
    selectedCategories: Map<string, boolean>,
    rootCategoryId: string | null
): TreeItemData {
    return {
        id: refCategory.id || "",
        rootId: rootCategoryId || "",
        text: refCategory.name || "",
        code: refCategory.code || "",
        selected: !!refCategory.id && !!selectedCategories.get(refCategory.id),
        checked: false,
        relevant: false,
        parent: null,
        nodes: subNodes
    }
}

function mapAllCategories2Nodes(
    parent: TreeItemData,
    refCategories: Immutable<RefCategory[]>,
    selectedCategories: Map<string, boolean>,
    rootCategoryId: string | null
): TreeItemData[] {
    // console.log('mapAllCategories2Nodes ' + parent.id)
    return refCategories.map((refCategory) => {
        const node = makeNode(refCategory, [], selectedCategories, rootCategoryId)
        node.parent = parent
        if (node.selected) selectAllParents(node)
        if (refCategory.subCategories) {
            node.nodes = mapAllCategories2Nodes(node, refCategory.subCategories, selectedCategories, rootCategoryId)
        }
        return node
    })
}

function mapCategory2Node(
    refCategory: Immutable<RefCategory>,
    filter: string,
    selectedCategories: Map<string, boolean>,
    rootCategoryId: string | null
): TreeItemData | null {
    // console.log('mapping ' + refCategory.name)
    let currentNode: TreeItemData | null = null

    if (isEligible(refCategory, filter)) {
        currentNode = makeNode(refCategory, [], selectedCategories, rootCategoryId)
        currentNode.relevant = !!filter
        if (refCategory.subCategories) {
            currentNode.nodes = mapAllCategories2Nodes(currentNode, refCategory.subCategories, selectedCategories, rootCategoryId)
        }

        // if (currentNode != null) {
        //     console.log('mapping ' + refCategory.name + ' has ' + (currentNode as TreeItemData).nodes.length + ' nodes')
        // }
        return currentNode
    }

    if (!isNullOrEmptyRO(refCategory.subCategories)) {
        refCategory.subCategories?.forEach((refSubCategory) => {
            const subNode = mapCategory2Node(refSubCategory, filter, selectedCategories, rootCategoryId)
            if (subNode) {
                if (!currentNode) {
                    // create current node on demand
                    currentNode = makeNode(refCategory, [], selectedCategories, rootCategoryId)
                }
                subNode.parent = currentNode
                if (subNode.selected) selectAllParents(subNode)
                currentNode.nodes.push(subNode)
            }
        })
        // if (currentNode != null) {
        //     console.log('mapping ' + refCategory.name + ' has ' + (currentNode as TreeItemData).nodes.length + ' nodes')
        // }
    }

    return currentNode
}

function mapCategories2Nodes(
    refCategories: Immutable<RefCategory[]> | null,
    filter: string,
    selectedCategories: Map<string, boolean>
): TreeData {
    const nodes: TreeItemData[] = []

    // if (refCategories && refCategories.length > 0) {
    //     const node = mapCategory2Node(refCategories[0], filter, selectedCategories)
    //     if (node) {
    //         nodes.push(node)
    //     }
    // }

    refCategories?.forEach((refCategory) => {
        const node = mapCategory2Node(refCategory, filter, selectedCategories, refCategory.id)
        if (node) {
            nodes.push(node)
        }
    })
    return { nodes }
}

function selectedCategoriesToMap(selCategories: Immutable<Category[]> | null): Map<string, boolean> {
    // console.log("Calculate the map")
    let selection = new Map()
    selCategories?.forEach((cat) => {
        selection.set(cat.id, true)
    })
    return selection
}

function isCategoryCountable(countableCategories: Immutable<string[]>, categoryId: string): boolean {
    return countableCategories.includes(categoryId)
}

function countSelected(
    root: TreeData,
    selected: Map<string, boolean>,
    countableCategories: Immutable<string[]>,
    refDataState: ReferenceDataState
): number {
    let counter = 0
    selected.forEach((_, selectedId) => {
        const rootId = refDataState.findRootByCategoryId(selectedId)?.id ?? ""
        if (isCategoryCountable(countableCategories, rootId)) counter++
    })
    return counter
}

interface CategoriesContainsProps {
    selectedCategoriesStates: State<Category[]>
    refCategories: Immutable<RefCategory[]>
    limit?: number
    countableCategories: Immutable<string[]>
    setCount?: (value: number) => void
}

const CategoriesContents: React.FC<CategoriesContainsProps> = ({
    selectedCategoriesStates,
    refCategories,
    limit = 0,
    countableCategories,
    setCount
}) => {
    const branding = useLanguageState().getStrings()
    const notifState = useNotificationsDataState()
    const [filter, setFilter] = useState("")
    const refDataState = useReferenceDataState()
    const selected = useMemo(() => selectedCategoriesToMap(selectedCategoriesStates.get()), [selectedCategoriesStates])
    const root = useMemo(() => mapCategories2Nodes(refCategories, filter, selected), [refCategories, filter, selected])

    useEffect(() => {
        if (!refDataState || !selected || selected.size === 0 || !refCategories || refCategories.length === 0) return
        selected.forEach((_, selectedId) => {
            const cat = refDataState.getCategory(selectedId)
            if (cat?.synthetic) removeElementById<Category>(selectedCategoriesStates, selectedId)
        })
    }, [refCategories, selected, selectedCategoriesStates]) // eslint-disable-line

    useEffect(() => {
        if (setCount) {
            setCount!(countSelected(root, selected, countableCategories, refDataState))
        }
        // eslint-disable-next-line
    }, [selected])

    const onSelectionChangedHandler = useCallback(
        (id: string, rootId: string) => {
            const selectionCount = countSelected(root, selected, countableCategories, refDataState)

            if (setCount) {
                setCount!(selectionCount)
            }

            const toRemoveCheck: boolean = selected.get(id) || false
            if (!toRemoveCheck && isCategoryCountable(countableCategories, rootId) && selectionCount >= limit) {
                console.log("Already at full capacity for ", rootId)
                const warningMessage = replacePlaceholder(branding.configuration.countableCategories.warningMessage, {
                    limit: String(limit)
                })
                notifState.addWarning(warningMessage)
                return
            }

            toRemoveCheck
                ? removeElementById<Category>(selectedCategoriesStates, id)
                : addElement<Category>(selectedCategoriesStates, { id })
        },
        // eslint-disable-next-line
        [
            branding.configuration.countableCategories.warningMessage,
            countableCategories,
            limit,
            notifState,
            refDataState,
            root,
            selected,
            selectedCategoriesStates
        ]
    )

    const onChangeSearchHandler = useCallback((searchFilter: string) => {
        setFilter(searchFilter)
    }, [])

    return (
        <>
            <SearchField onChange={onChangeSearchHandler} />
            <TreeCheck
                showCode={branding.configuration.showCategoriesCode}
                rootNode={root}
                selection={selected}
                onSelectionChanged={onSelectionChangedHandler}
            />
        </>
    )
}

export default CategoriesContents
